import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import React, { useState, useCallback, useEffect } from 'react';
import EditorSettings from '@wix/da-ds/pkg/Icons/24x24/EditorSettings';
import cn from 'classnames';
import SidePanel from '../../../../../components/menus/SidePanel/SidePanel';
import { useForm, useController } from 'react-hook-form';
import { getCurrentUser, getExtensionOptions, getExternalRenderer, getStoredCallback } from '../../../../../components/utils/tiptap';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import Flex from '@wix/da-ds/pkg/Flex';
import { useTranslation } from 'react-i18next';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import RefreshIcon from '@wix/da-ds/pkg/Icons/24x24/EditorRefresh';
import ModalHeader from '@wix/da-ds/pkg/modals/dsPresets/Modal/ModalHeader';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import NumericField from '../../../../../components/lego/NumericField';
import { addMissingProtocol, validateHref } from '../../../../../components/utils/validateHref';
import { TOOLTIP_HOVER_DELAY } from '../../../../../constants';
import EditorIconButton from '../../../../../components/menus/_partials/EditorIconButton';
import buttonStyles from '../../../../../components/menus/buttons/buttons.scss';
import s from './Settings.scss';
const Settings = ({ editor })=>{
    var _attrs_deviation_author, _attrs_deviation;
    const { t } = useTranslation('editor');
    const attrs = editor.getAttributes('da-deviation');
    const options = getExtensionOptions(editor, 'da-deviation');
    const Preview = getExternalRenderer(editor, 'deviation');
    const [showSettings, setShowSettings] = useState(false);
    const { control, errors, trigger, setValue } = useForm();
    var _attrs_width;
    const { field: widthRange } = useController({
        name: 'width',
        control,
        defaultValue: (_attrs_width = attrs.width) !== null && _attrs_width !== void 0 ? _attrs_width : options.defaultWidth
    });
    const { field: widthToggle } = useController({
        name: 'widthToggle',
        control,
        defaultValue: !attrs.width
    });
    var _attrs_link;
    const { field: link } = useController({
        name: 'link',
        control,
        defaultValue: (_attrs_link = attrs.link) !== null && _attrs_link !== void 0 ? _attrs_link : '',
        rules: {
            validate: (value)=>{
                return value.length === 0 || validateHref(addMissingProtocol(value));
            }
        }
    });
    const onWidthToggleChange = useCallback((e)=>{
        widthToggle.onChange(e);
        if (e) {
            editor.commands.updateAttributes('da-deviation', {
                width: null
            });
            widthRange.onChange(options.defaultWidth);
        }
    }, [
        editor.commands,
        options.defaultWidth,
        widthRange,
        widthToggle
    ]);
    const onWidthChange = useCallback((e)=>{
        editor.commands.updateAttributes('da-deviation', {
            width: e
        });
        widthRange.onChange(e);
    }, [
        editor.commands,
        widthRange
    ]);
    const onLinkChange = useCallback((e)=>{
        link.onChange(e);
        trigger('link');
        if (e) {
            const href = e.target.value.trim();
            if (href.length === 0 || validateHref(addMissingProtocol(href))) {
                editor.commands.updateAttributes('da-deviation', {
                    link: href === '' ? null : addMissingProtocol(href)
                });
            }
        }
    }, [
        editor.commands,
        link,
        trigger
    ]);
    const replaceDeviation = useCallback(()=>{
        const onSelectMedia = getStoredCallback(editor, 'onSelectMedia');
        onSelectMedia && onSelectMedia((deviations)=>{
            if (deviations.length > 0) {
                editor.chain().focus().daDeviationReplace(deviations[0]).run();
            }
        }, {});
    }, [
        editor
    ]);
    const refreshDeviation = useCallback(()=>{
        editor.chain().focus().daDeviationRefresh().run();
    }, [
        editor
    ]);
    const closeSettings = ()=>{
        setShowSettings(false);
    };
    const currentUser = getCurrentUser(editor);
    const allowLink = (attrs === null || attrs === void 0 ? void 0 : (_attrs_deviation = attrs.deviation) === null || _attrs_deviation === void 0 ? void 0 : (_attrs_deviation_author = _attrs_deviation.author) === null || _attrs_deviation_author === void 0 ? void 0 : _attrs_deviation_author.userId) === (currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId);
    useEffect(()=>{
        var _attrs_width;
        setValue('width', (_attrs_width = attrs.width) !== null && _attrs_width !== void 0 ? _attrs_width : options.defaultWidth);
        setValue('widthToggle', !attrs.width);
    }, [
        attrs.width,
        options.defaultWidth,
        setValue
    ]);
    var _options_formMaxWidth, _ref, _options_minWidth;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, showSettings && /*#__PURE__*/ React.createElement(SidePanel, {
        editor: editor,
        onRequestClose: closeSettings,
        header: /*#__PURE__*/ React.createElement(ModalHeader, {
            heading: t('ext.deviation.settings.title')
        }),
        footer: /*#__PURE__*/ React.createElement(SolidButton, {
            size: "medium",
            variant: "approval-green",
            onClick: closeSettings
        }, t('ext.deviation.settings.btn.done'))
    }, /*#__PURE__*/ React.createElement(Flex, {
        direction: "column",
        gap: 8
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['preview']
    }, /*#__PURE__*/ React.createElement(Preview, {
        deviation: attrs.deviation,
        height: 200,
        width: 250
    }), /*#__PURE__*/ React.createElement("div", {
        className: s['replace-refresh-btn-wrapper']
    }, /*#__PURE__*/ React.createElement(IconButton, {
        className: cn(s['btn']),
        size: 'small',
        icon: EditIcon,
        tooltipOffset: [
            0,
            8
        ],
        tooltipText: t('ext.deviation.settings.replace'),
        "aria-label": t('ext.deviation.settings.replace'),
        onClick: replaceDeviation
    }), /*#__PURE__*/ React.createElement(IconButton, {
        className: cn(s['btn']),
        size: 'small',
        icon: RefreshIcon,
        tooltipOffset: [
            0,
            8
        ],
        tooltipText: t('ext.deviation.settings.refresh'),
        "aria-label": t('ext.deviation.settings.refresh'),
        onClick: refreshDeviation
    }))), options.canResize && /*#__PURE__*/ React.createElement("fieldset", {
        className: s['size']
    }, /*#__PURE__*/ React.createElement("legend", null, t('ext.deviation.settings.size.legend')), /*#__PURE__*/ React.createElement(BooleanInput, _object_spread_props(_object_spread({
        id: "widthToggle",
        label: t('ext.deviation.settings.size.range.label'),
        type: "toggle"
    }, widthToggle), {
        onChange: onWidthToggleChange,
        noBottomSpacing: true
    })), !widthToggle.value && /*#__PURE__*/ React.createElement("div", {
        className: s['width-range']
    }, /*#__PURE__*/ React.createElement(InputLabelText, {
        className: s['legend']
    }, t('ext.gallery.settings.container')), /*#__PURE__*/ React.createElement(NumericField, _object_spread_props(_object_spread({
        label: t('ext.gallery.settings.container.width')
    }, widthRange), {
        onChange: onWidthChange,
        maxValue: (_ref = (_options_formMaxWidth = options.formMaxWidth) !== null && _options_formMaxWidth !== void 0 ? _options_formMaxWidth : options.maxWidth) !== null && _ref !== void 0 ? _ref : 780,
        minValue: (_options_minWidth = options.minWidth) !== null && _options_minWidth !== void 0 ? _options_minWidth : 10,
        sliderStep: 1
    })))), options.canLink && allowLink && /*#__PURE__*/ React.createElement(TextInput, _object_spread_props(_object_spread({
        id: "link",
        label: t('ext.deviation.settings.link.label'),
        placeholder: t('ext.deviation.settings.link.placeholder')
    }, link), {
        onChange: onLinkChange,
        validationResult: errors.link ? 'error' : 'success',
        details: errors.link ? 'Invalid Link' : undefined,
        onFocus: (e)=>{
            setTimeout(()=>{
                e.target.scrollIntoView();
            }, 300);
        }
    })))), /*#__PURE__*/ React.createElement(EditorIconButton, {
        featureId: "node:deviation:settings",
        icon: EditorSettings,
        onClick: ()=>{
            setShowSettings(true);
        },
        className: cn(buttonStyles['button']),
        tooltipHoverDelay: TOOLTIP_HOVER_DELAY,
        tooltipText: t('ext.deviation.toolbar.settings'),
        "aria-label": t('ext.deviation.toolbar.settings')
    }));
};
Settings.displayName = 'Settings';
export default Settings;

//# sourceMappingURL=Settings.js.map